import { format } from "date-fns"
import { zonedTimeToUtc } from "date-fns-tz"
import { enGB, fr, es, de } from "date-fns/locale"
import isValid from "date-fns/isValid"
import { getLanguage, isEnglishLanguage } from "utils/i18n"
import { subDays, add, getSeconds, getMinutes, addDays, isSameDay, sub } from "date-fns"
import parseISO from "date-fns/parseISO"

export const DEFAULT_DATE_EU_FORMAT = "dd/MM/yyyy HH:mm"
export const DEFAULT_DATE_UK_FORMAT = "MM/dd/yyyy hh:mm a"

export const DEFAULT_SHORT_DATE_EU_FORMAT = "dd/MM/yyyy"
export const DEFAULT_SHORT_DATE_UK_FORMAT = "MM/dd/yyyy"

export const DEFAULT_SHORT_HOUR_EU_FORMAT = "HH:mm"
export const DEFAULT_SHORT_HOUR_UK_FORMAT = "hh:mm a"

const dateService = {
    i18nLocale: {
        "fr": fr,
        "en": enGB,
        "es": es,
        "de": de
    }[getLanguage()],

    getCurrentDate(stringFormat = null) {
        return stringFormat
            ? format(new Date(), stringFormat, { locale: this.i18nLocale })
            : new Date()
    },

    getUTCDate(date: string) {
        // https://stackoverflow.com/questions/65183368/proper-way-to-parse-a-date-as-utc-using-date-fns
        return zonedTimeToUtc(date, "UTC")
    },

    getDate(date: string) {
        // Fix for safari
        const formatter = this.formatDate(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
        return new Date(formatter)
    },

    getCurrentDateFormat() {
		return isEnglishLanguage() ? DEFAULT_DATE_UK_FORMAT : DEFAULT_DATE_EU_FORMAT
	},

	getCurrentShortDateFormat() {
		return isEnglishLanguage() ? DEFAULT_SHORT_DATE_UK_FORMAT : DEFAULT_SHORT_DATE_EU_FORMAT
	},

	getCurrentHourDateFormat() {
		return isEnglishLanguage() ? DEFAULT_SHORT_HOUR_UK_FORMAT : DEFAULT_SHORT_HOUR_EU_FORMAT
	},
    
    getTimestamp(date: string) {
        return this.getDate(date).getTime()
    },

	formatDate(date: string | Date, stringFormat: string, neededGetUTCDate = true) {
		const dateUTC = typeof date === "string" ? this.getUTCDate(date) : date
		return format(new Date(dateUTC), stringFormat, { locale: this.i18nLocale })
	},

    addDays(date: string, numberOfDays: number) {
        return addDays(new Date(date), numberOfDays)
    },

    subDays(date: string, duration: number) {
        return subDays(new Date(date), duration)
    },

    sub(date: string, duration: Duration) {
        return sub(new Date(date), duration)
    },

	isSameDay(dateA: string, dateB: string) {
		return isSameDay(new Date(dateA), new Date(dateB))
    },

    formatDateToUTC(date: string) {
        return date ? zonedTimeToUtc(date, "Europe/Paris").toISOString() : null
    },

    isValidDate(date: string) {
        return isValid(date)
    },

    // Manage timer
    getMinutesAndSecondsString(date: string) {
        const minutes = getMinutes(new Date(date))
        const seconds = getSeconds(new Date(date))
        return `${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
    },

	// ------------------------
	// NB: Dates are managed in UTC format in the backend, but are shown through a timezone in the frontend.
	// All dates sent to the backend should be in UTC format.
	// All dates received from the backend should be considered as in UTC format.
	// As a reminder, the built-in object `new Date()` always return a local (timezone) date, but
	// internally manage an UTC date 🤯
	// ------------------------
	// ------------------------
	// CONVERT DATES
	// ------------------------

	// from local Tz (eg new Date()) to UTC
	getUTCDateFromTz(tzDate: string | Date): Date {
		const dateToParse = typeof tzDate === "string" ? tzDate : tzDate.toISOString()
		const parsedDate = parseISO(dateToParse)
		const UTCDate = add(parsedDate, {
			minutes: new Date().getTimezoneOffset(),
		})
		return UTCDate
	},

	// ------------------------
	// GET CURRENT DATE
	// ------------------------
	getCurrentDateUTC(stringFormat: string = null) {
		const currentDate = this.getUTCDateFromTz(new Date())
		return stringFormat ? format(currentDate, stringFormat) : currentDate
	}
}

export default dateService